import { useState } from 'react';
import { Link } from '@remix-run/react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '~/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { LoginForm } from '~/components/loginForm';
import { SignupForm } from '~/components/signupForm';

export interface AuthenticationDialogProps {
  open: boolean;
  onClose: () => void;
  redirectTo?: string;
  title?: string;
  description?: string;
  defaultTab?: 'login' | 'signup';
}

/**
 * Reusable authentication dialog component
 * Shows login/signup tabs in a modal
 */
export function AuthenticationDialog({
  open,
  onClose,
  redirectTo = '/cuenta/espacios/nuevo',
  title,
  description,
  defaultTab = 'signup',
}: AuthenticationDialogProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(defaultTab);

  // Reset tab when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      // Reset to default tab after a short delay (after animation)
      setTimeout(() => setActiveTab(defaultTab), 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {title || (activeTab === 'signup' ? '¡Último paso!' : 'Inicia sesión')}
          </DialogTitle>
          <DialogDescription>
            {description || (
              activeTab === 'signup' 
                ? 'Crea tu cuenta para guardar y publicar tu espacio'
                : 'Accede a tu cuenta para continuar'
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'login' | 'signup')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signup">Crear cuenta</TabsTrigger>
            <TabsTrigger value="login">Iniciar sesión</TabsTrigger>
          </TabsList>

          <TabsContent value="signup" className="mt-4">
            <SignupForm redirectTo={redirectTo} />
          </TabsContent>

          <TabsContent value="login" className="mt-4">
            <LoginForm redirectTo={redirectTo} />
          </TabsContent>
        </Tabs>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          Al continuar, aceptas nuestros{' '}
          <Link to="/politica-de-privacidad" className="underline hover:text-foreground" target="_blank">
            términos y condiciones
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Hook to manage authentication dialog state
 */
export function useAuthDialog(defaultRedirectTo?: string) {
  const [isOpen, setIsOpen] = useState(false);
  const [redirectTo, setRedirectTo] = useState(defaultRedirectTo || '/cuenta/espacios/nuevo');
  const [defaultTab, setDefaultTab] = useState<'login' | 'signup'>('signup');

  const open = (options?: { 
    redirectTo?: string; 
    defaultTab?: 'login' | 'signup';
  }) => {
    if (options?.redirectTo) setRedirectTo(options.redirectTo);
    if (options?.defaultTab) setDefaultTab(options.defaultTab);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    open,
    close,
    redirectTo,
    defaultTab,
  };
}
